import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';

import { KeypadButton } from '@sparx/sw-common';
import { VelocityTransitionGroup } from 'velocity-react';
import { getKeyPressed } from 'phoenix2/utils/keyboard';
import { getConfigParameter } from 'phoenix2/state/config-params/selectors';

const GRID_KEYPAD_MIN_HEIGHT = 500;
const LINE_KEYPAD_MIN_WIDTH = 450;

/**
 * Component for a full-size keypad where the keys are in a 4x4 grid, used
 * for larger screen heights
 */
const GridKeypad = ({
    createButton,
    enableBackspace,
    allowMinusSign,
    enableMinusSign,
    allowEnter,
    enableEnter,
    allowDecimalPoint,
    enableDecimalPoint,
}) => (
    <div className="keypad keypad-grid">
        <div className="keypad-row">
            {createButton('7')}
            {createButton('8')}
            {createButton('9')}
            {/* Backspace is always present */}
            {createButton('Backspace', enableBackspace)}
        </div>
        <div className="keypad-row">
            {createButton('4')}
            {createButton('5')}
            {createButton('6')}
            {allowMinusSign ? (
                createButton('-', enableMinusSign)
            ) : (
                <div className="button-container" />
            )}
        </div>
        <div className="keypad-row">
            {createButton('1')}
            {createButton('2')}
            {createButton('3')}
            <div className="button-container" />
        </div>
        <div className="keypad-row">
            {createButton('0')}
            {allowDecimalPoint && createButton('.', enableDecimalPoint)}
            {allowEnter ? createButton('Enter', enableEnter) : <div className="button-container" />}
        </div>
    </div>
);

GridKeypad.propTypes = {
    createButton: PropTypes.func.isRequired,
    allowMinusSign: PropTypes.bool.isRequired,
    allowDecimalPoint: PropTypes.bool.isRequired,
    allowEnter: PropTypes.bool.isRequired,
    enableMinusSign: PropTypes.bool.isRequired,
    enableDecimalPoint: PropTypes.bool.isRequired,
    enableEnter: PropTypes.bool.isRequired,
    enableBackspace: PropTypes.bool.isRequired,
};

GridKeypad.displayName = 'GridKeypad';

/**
 * Component for the single-line keypad where the keys all on the same row, used
 * for smaller screen heights
 */
const LineKeypad = ({
    createButton,
    enableBackspace,
    allowMinusSign,
    enableMinusSign,
    allowEnter,
    enableEnter,
    allowDecimalPoint,
    enableDecimalPoint,
}) => (
    <div className="keypad keypad-line">
        <div className="keypad-row">
            {createButton('1')}
            {createButton('2')}
            {createButton('3')}
            {createButton('4')}
            {createButton('5')}
            {createButton('6')}
            {createButton('7')}
            {createButton('8')}
            {createButton('9')}
            {createButton('0')}
            {allowDecimalPoint && createButton('.', enableDecimalPoint)}
            {allowMinusSign && createButton('-', enableMinusSign)}
            {createButton('Backspace', enableBackspace)}
            {allowEnter && createButton('Enter', enableEnter)}
        </div>
    </div>
);

LineKeypad.propTypes = {
    createButton: PropTypes.func.isRequired,
    allowMinusSign: PropTypes.bool.isRequired,
    allowDecimalPoint: PropTypes.bool.isRequired,
    allowEnter: PropTypes.bool.isRequired,
    enableMinusSign: PropTypes.bool.isRequired,
    enableDecimalPoint: PropTypes.bool.isRequired,
    enableEnter: PropTypes.bool.isRequired,
    enableBackspace: PropTypes.bool.isRequired,
};

LineKeypad.displayName = 'LineKeypad';

class PureKeypadElement extends React.Component {
    static displayName = 'PureKeypadElement';

    static propTypes = {
        onButtonPressed: PropTypes.func.isRequired,
        handleKeyboard: PropTypes.bool.isRequired,
        allowMinusSign: PropTypes.bool.isRequired,
        allowDecimalPoint: PropTypes.bool.isRequired,
        allowEnter: PropTypes.bool.isRequired,
        enableMinusSign: PropTypes.bool.isRequired,
        enableDecimalPoint: PropTypes.bool.isRequired,
        enableEnter: PropTypes.bool.isRequired,
        enableBackspace: PropTypes.bool.isRequired,
        enableAnimation: PropTypes.bool.isRequired,
        gridKeypadMinHeight: PropTypes.number.isRequired,

        // Whether to show the keypad in "grid" mode or "line" mode, if not
        // set, will decide based on screen size
        layout: PropTypes.string,

        // Custom style to put on the keypad object
        style: PropTypes.object,

        // Whether to disable animations:
        disableAnimations: PropTypes.bool,
    };

    static defaultProps = {
        handleKeyboard: false,
        allowEnter: false,
        allowMinusSign: true,
        allowDecimalPoint: true,
        enableMinusSign: true,
        enableDecimalPoint: true,
        enableEnter: false,
        enableBackspace: true,
        enableAnimation: true,
        gridKeypadMinHeight: GRID_KEYPAD_MIN_HEIGHT,
    };

    constructor(props) {
        super(props);

        this.state = {
            useLineKeypad: false,
        };
    }

    componentDidMount() {
        // If we need to handle the keyboard, but we don't have an enter
        // button, we need to listen for the enter key being pressed
        if (this.props.handleKeyboard && !this.props.allowEnter) {
            this.keyDownListener = evt => {
                if (getKeyPressed(evt) === 'Enter') {
                    this.props.onButtonPressed(evt, 'Enter');
                }
            };
            document.addEventListener('keydown', this.keyDownListener, false);
        }

        // Check the screen size to see if the grid keypad or line keypad
        // should be shown. Also add a resize listener to check again if the
        // screen size changes
        this.checkScreenSize();
        this.resizeListener = () => this.checkScreenSize();
        window.addEventListener('resize', this.resizeListener);
    }

    componentWillUnmount() {
        if (this.keyDownListener) {
            document.removeEventListener('keydown', this.keyDownListener);
        }

        if (this.resizeListener) {
            window.removeEventListener('resize', this.resizeListener);
        }
    }

    componentDidUpdate() {
        this.checkScreenSize();
    }

    /**
     * Determine whether the grid keypad or line keypad should be used, and
     * set state accordingly
     */
    checkScreenSize() {
        const { layout } = this.props;

        const useLineKeypad =
            layout === 'line' ||
            (!layout &&
                window.innerHeight < this.props.gridKeypadMinHeight &&
                window.innerWidth >= LINE_KEYPAD_MIN_WIDTH);

        if (useLineKeypad !== this.state.useLineKeypad) {
            this.setState({ useLineKeypad });
        }
    }

    render() {
        const { enableAnimation, layout, style, disableAnimations } = this.props;
        const { useLineKeypad } = this.state;

        const keypad = useLineKeypad ? (
            <LineKeypad {...this.props} createButton={this.createButton.bind(this)} />
        ) : (
            <GridKeypad {...this.props} createButton={this.createButton.bind(this)} />
        );

        const containerClassName = classNames('keypad-container', { 'keypad-autosize': !layout });
        const animationContainerClassName = classNames('keypad-animation-container', {
            'keypad-animation-container-line': useLineKeypad,
        });

        let transitionGroup = null;

        if (!disableAnimations) {
            transitionGroup = (
                <VelocityTransitionGroup
                    className={animationContainerClassName}
                    style={style}
                    enter={enableAnimation && 'transition.expandIn'}
                    leave={enableAnimation && 'transition.expandOut'}
                    runOnMount={true}
                >
                    <div className={containerClassName}>{keypad}</div>
                </VelocityTransitionGroup>
            );
        } else {
            transitionGroup = (
                <div className={animationContainerClassName} style={style}>
                    <div className={containerClassName}>{keypad}</div>
                </div>
            );
        }

        return transitionGroup;
    }

    createButton(character, enabled = true) {
        return (
            <KeypadButton
                character={character}
                onButtonPressed={this.props.onButtonPressed}
                handleKeyboard={this.props.handleKeyboard}
                enabled={enabled}
            />
        );
    }
}

const mapStateToProps = state => ({
    disableAnimations: getConfigParameter(state, 'disableAnimations'),
});

export const KeypadElement = connect(mapStateToProps, null, null, { forwardRef: true })(
    PureKeypadElement,
);
